/**
 * STILLSUN sky — a sun angle, a moon, and a four-stop colour ramp.
 *
 * This replaces the single-scattering LUT bake that shipped for the extraction shooter. That
 * model existed to make one fixed hour of one dusty afternoon read correctly; the game now runs
 * a four-hour day (`@stillsun/shared`'s `worldclock.ts`) and needs a sky that is *continuous*
 * across every minute of it, cheap enough to re-evaluate every frame, and — the owner's note —
 * bold with hue rather than another amber glow on charcoal.
 *
 * So: no LUT, no PMREM, no IBL, no aerial-perspective texture fetch, no dust. What is left is
 *
 *   1. **One dome, one draw.** A TSL gradient from a horizon colour to a zenith colour, plus an
 *      analytic sun disc with a warm halo, an opposing moon disc with a cool one, and a starfield
 *      that only exists at night. Every colour in it is a uniform.
 *   2. **A four-stop ramp** — sunrise, day, sunset, night — each stop naming six colours (key
 *      light, zenith, horizon, hemisphere sky, hemisphere ground, fog) and four scalars (key and
 *      ambient strength, fog near and far).
 *   3. **Plain range fog, whose range the ramp owns.** `scene.fogNode` is the ordinary
 *      `fog(colour, rangeFogFactor(near, far))` three would have built itself; `installFog` binds
 *      colour, near and far through `reference(...)`, so all three are uniforms and `update`
 *      rewriting them every frame recompiles nothing.
 *
 * ## Bonelight, and why fog is not the horizon any more
 *
 * The approved colour story (Option C, "Bonelight" — a bone-white sky over sage ridges) has one
 * failure mode written on its own design board: *fog matched to the sky erases distant hills into
 * white and flattens depth, so near and far hills sit at the same value.* The previous ramp made
 * exactly that mistake structurally — it had **one** colour serving as both the fog and the dome's
 * horizon band, which is free depth-matching when the sky is a saturated cyan and a disaster when
 * it is bone.
 *
 * So `horizon` and `fog` are now separate stops-columns, and the whole depth read of the Downs
 * hangs on the gap between them. Day: horizon `#FBF7EC` (L* 97.2) against fog `#A8B0A6` (L* 70.9)
 * — 26 points of lightness, so a ridge saturated to pure fog colour is still a solid mass against
 * the sky. The fog resolves *toward the ridge* (`#93A199`, L* 64.9, six points off the fog) rather
 * than toward the sky, which is the entire point of the pair.
 *
 * The stops are keyed off `nightFactor` / `twilightFactor`, not off named hours: the world clock's
 * phase boundaries move with `SUNRISE_T`/`SUNSET_T`, and both factors are already continuous
 * functions of the sun's elevation, so there is nothing here to keep in sync with them.
 *
 * ## Blending, and why there is never a hard cut
 *
 * `nightFactor(t)` is 0 by day and 1 at the deepest part of the night, eased across the twenty
 * degrees either side of the horizon. `twilightFactor(t)` is 0 away from the horizon and 1 at the
 * crossing. So the ramp is two mixes:
 *
 *     base  = mix(DAY, NIGHT, nightFactor)
 *     final = mix(base, t < 0.5 ? SUNRISE : SUNSET, twilightFactor)
 *
 * Both factors are continuous functions of the sun's elevation, and the `t < 0.5` discriminator
 * only ever flips at noon — where `twilightFactor` is exactly 0 — so the whole cycle is smooth.
 *
 * ## Night is lit, not black
 *
 * `keyDir` follows whichever body is above the horizon: the sun by day, the moon (which sits
 * exactly opposite it) by night. The swap happens at the instant the sun's elevation crosses
 * zero, which is also the instant `keyIntensity` is 0 — `HORIZON_GATE` fades the key light out
 * over the last ten degrees — so the 180-degree swing of the shadow direction happens while there
 * is no directional light to swing. Twilight is carried entirely by the hemisphere fill.
 *
 * At night the key is the moon at `NIGHT.key` of the design sun, and it is deliberately **bone
 * white** (`#DFE6DC`) rather than a blue moonlight cliché: bone is the material this world is made
 * of, and Bonelight's other acceptance test is that bone stays high-value after dark. The value
 * drop through sunset is carried by the *hemisphere* instead — sky and ground bounce fall from
 * `#F6F0E2`/`#D3CDB8` to `#241C33`/`#14121B` — so a surface turned away from the moon goes almost
 * black while a surface facing it stays pale. That split is what gives the mint lanterns
 * (`#1F8F5E`) and the Choir's rust (`#C4362B`) something to glow against, and it is why night is a
 * slow turn rather than a cut: the ramp's own night fog `near` of 60 m means everything inside
 * silhouette range is still lit by the moon, and only the distance closes in.
 *
 * ## What the caller has to do
 *
 * `update(unixMs)` writes uniforms and lerps a handful of `Color`s. It never changes a light
 * count and never toggles a light's `visible` — three hashes the visible light set into every
 * material's cache key, so either of those rebuilds every material in the scene
 * (docs/WEBGPU-NOTES.md §3). The scene's `DirectionalLight` and `HemisphereLight` must be driven
 * from `keyDir` / `keyIntensity` / `sunColor` / `skyColor` / `hemiColor` / `hemiIntensity`, all of
 * which are plain fields re-written in place each frame.
 */
import {
  BackSide,
  BoxGeometry,
  Color,
  Mesh,
  NodeMaterial,
  SRGBColorSpace,
  Scene,
  Vector3,
  type Fog,
  type Renderer,
  type Texture,
} from 'three/webgpu';
import * as TSL from 'three/tsl';
import {
  clockText,
  nightFactor,
  sunDirection,
  sunElevation,
  timeOfDay,
  twilightFactor,
} from '@stillsun/shared';
import type { QualityTier } from './renderer.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyNode = any;
type NodeFn = (...args: AnyNode[]) => AnyNode;

/**
 * Loosely typed TSL surface, the same aliases `clustered.ts` uses. three's TSL typings reject a
 * lot of perfectly valid mixed float/vec3 arithmetic and there is no value in a wall of casts.
 */
const {
  Fn,
  float,
  vec4,
  uniform,
  mix,
  max,
  dot,
  normalize,
  smoothstep,
  pow,
  clamp,
  reference,
  rangeFogFactor,
  fog,
  mx_noise_float,
} = TSL as unknown as Record<
  | 'Fn'
  | 'float'
  | 'vec4'
  | 'uniform'
  | 'mix'
  | 'max'
  | 'dot'
  | 'normalize'
  | 'smoothstep'
  | 'pow'
  | 'clamp'
  | 'reference'
  | 'rangeFogFactor'
  | 'fog'
  | 'mx_noise_float',
  NodeFn
>;
const positionWorld: AnyNode = TSL.positionWorld;
const cameraPosition: AnyNode = TSL.cameraPosition;
const modelViewProjection: AnyNode = TSL.modelViewProjection;

// --- the palette -----------------------------------------------------------------------------

/**
 * One stop of the day. Six colours and four scalars; every visible property of the sky, of the
 * scene's two lights and of the distance fog is a blend of these across at most two stops.
 *
 * The six colours are the approved Bonelight table, verbatim for the day stop:
 *
 * | column      | what it drives                                                          |
 * | ----------- | ----------------------------------------------------------------------- |
 * | `sun`       | the key light's colour — the sun's, or the moon's in the night stop      |
 * | `zenith`    | the top of the dome                                                     |
 * | `horizon`   | the bottom of the dome, **not** the fog                                 |
 * | `sky`       | the `HemisphereLight`'s sky colour                                      |
 * | `hemi`      | the `HemisphereLight`'s ground colour — the fill under a shadowed face  |
 * | `fog`       | distance fog; the value that decides whether a far ridge reads at all   |
 *
 * `hemi` is the number to touch if night ever reads as blind: it is the only fill under a surface
 * turned away from the moon. `fog` is the number to touch if depth flattens: see the header on why
 * it must stay well clear of `horizon`.
 */
interface SkyStop {
  /** Key light — the sun, or (in the night stop) the moon. */
  readonly sun: number;
  /** Top of the dome. */
  readonly zenith: number;
  /** Bottom of the dome. Deliberately not the fog colour — see the header. */
  readonly horizon: number;
  /** The hemisphere light's sky colour. */
  readonly sky: number;
  /** Ground bounce — the hemisphere light's ground colour. */
  readonly hemi: number;
  /** Distance fog. */
  readonly fog: number;
  /** Key intensity as a fraction of the scene's design sun. */
  readonly key: number;
  /** Hemisphere fill as a fraction of the scene's design ambient. */
  readonly ambient: number;
  /** Where the fog starts biting, in metres. Written into `scene.fog.near`. */
  readonly fogNear: number;
  /** Where the fog is total, in metres. Written into `scene.fog.far`. */
  readonly fogFar: number;
  /**
   * Broad wash of key colour across that half of the sky. Per-stop rather than constant because
   * the day sky is now nearly white: a wash tuned for a saturated cyan zenith clips a bone one to
   * flat paper across half the frame. Twilight, which is where a wash earns its keep, gets more.
   */
  readonly wash: number;
}

/** Cold pearl overhead warming to a bone-cream horizon; the first light is peach, not gold. */
const SUNRISE: SkyStop = {
  sun: 0xffe0b4,
  zenith: 0xcbd2d0,
  horizon: 0xf0dcc6,
  sky: 0xe8e4d6,
  hemi: 0xc3bca6,
  fog: 0x9ba8a4,
  key: 0.5,
  ambient: 0.95,
  fogNear: 140,
  fogFar: 400,
  wash: 0.2,
};

/**
 * Bonelight proper: a bone-white sky, a sage-grey haze, a warm heath bounce.
 *
 * **`key` is 0.62, not 1, and that is a Bonelight-specific number.** `scene.ts` multiplies it by
 * `SUN_INTENSITY` (12), which was tuned against the old amber-on-charcoal world where every
 * albedo was dark. Bonelight's are not: the Downs' bone-pale grass, the barrow earth and the
 * dark heath are 0.47-0.85 albedo, and at full key all three clipped to the same white — the
 * zone read as one flat sheet of paper from the foreground to the horizon, which is precisely
 * the "distant hills and near hills sit at nearly the same value" failure Option C's own board
 * predicted. Pulling the key back below the clip is what lets the four splat layers separate at
 * all; the sky, the fog and the hemisphere carry the brightness of the hour instead.
 */
const DAY: SkyStop = {
  sun: 0xfff6e2,
  zenith: 0xf6f0e2,
  horizon: 0xfbf7ec,
  sky: 0xf6f0e2,
  hemi: 0xd3cdb8,
  fog: 0xa8b0a6,
  key: 0.62,
  ambient: 0.85,
  fogNear: 180,
  fogFar: 460,
  wash: 0.06,
};

/**
 * The turn. Value falls hard here — this is the stop that buys the night its darkness — and the
 * hue swings violet over a scorched-earth bounce so the drop reads as an event rather than as a
 * dimmer being turned down.
 */
const SUNSET: SkyStop = {
  sun: 0xd9a077,
  zenith: 0x8e86a2,
  horizon: 0xe0ab80,
  sky: 0xb9a3d6,
  hemi: 0x94643f,
  fog: 0x7f6c97,
  key: 0.5,
  ambient: 0.9,
  fogNear: 110,
  fogFar: 340,
  wash: 0.3,
};

/**
 * Bone-white moon over a near-black plum sky. The key *colour* is high-value on purpose (bone
 * stays bone after dark); the darkness is all in `sky`/`hemi` and in how little of that white
 * key there is, so a moonlit face reads pale and a face turned away goes almost to nothing.
 * `fogNear` of 60 m keeps the whole silhouette range clear.
 *
 * **`key` is 0.07, and it was measured, not guessed.** `scene.ts` multiplies it by
 * `SUN_INTENSITY` (12), so the first pass's 0.16 put a bone-white light of intensity 1.92 on the
 * ground at midnight: rendered, Lanternfall's paving sat at roughly 0.65 value under a sky at
 * 0.08 and the city read as a floodlit field with a starfield painted over it — the mint lanterns
 * had nothing to glow against, which is precisely the failure Option C's own board predicted for
 * the night half of this palette. 0.07 lands the moon at 0.84 and puts the ground below the
 * lantern pools where it belongs. Readability at 60 m is carried by the hemisphere (`ambient: 1`
 * against the two dark hemisphere colours above), not by the key, which is the whole reason the
 * two are separate numbers.
 */
const NIGHT: SkyStop = {
  sun: 0xdfe6dc,
  zenith: 0x14091c,
  horizon: 0x2a2438,
  sky: 0x241c33,
  hemi: 0x14121b,
  fog: 0x241e33,
  key: 0.07,
  ambient: 1,
  fogNear: 60,
  fogFar: 260,
  wash: 0.02,
};

/**
 * How far above the horizon the lit body has to climb before the key light is at full strength,
 * as a sine of elevation. Ten degrees. Below it the key fades to nothing, which is what makes the
 * sun -> moon swap invisible: the direction flips at the moment the intensity is zero.
 */
const HORIZON_GATE = 0.174;

// --- the dome ---------------------------------------------------------------------------------

/** Dome scale. Shading is direction-only, so this just has to clear the far-plane trick's range. */
const DOME_SCALE = 450_000;

/**
 * Angular radius of the sun, radians. The real one is 0.27 deg; this is a fable sky and the disc
 * is meant to be legible as a *coin*, not as a highlight.
 */
const SUN_ANGULAR_RADIUS = (0.5 * Math.PI) / 180;
/** …and the moon's, which is deliberately the larger of the two. */
const MOON_ANGULAR_RADIUS = (0.95 * Math.PI) / 180;

/** Disc brightness relative to the ramp's key colour. Enough to tone-map to white and bloom. */
const SUN_DISC_GAIN = 26;
/** The moon is the brighter of the two *relative to its sky*, because its sky is nearly black. */
const MOON_DISC_GAIN = 4;

/**
 * Tight halo hugging the disc, then a broad wash across that half of the sky (the wash's gain is
 * per-stop — see `SkyStop.wash`). Both gains came down hard for Bonelight: the old 0.85/0.22 pair
 * was tuned against a `#2b8ad6` zenith with ~0.25 of linear headroom above it, and the day zenith
 * is now `#F6F0E2` at ~0.88 linear. Anything above ~0.1 added to that clips a third of the sky to
 * flat paper and hands the bloom a solid white plate.
 */
const SUN_HALO_POWER = 150;
const SUN_HALO_GAIN = 0.3;
const SUN_WASH_POWER = 8;
const MOON_HALO_POWER = 240;
const MOON_HALO_GAIN = 0.55;

/**
 * Horizon compression. The gradient is `mix(horizon, zenith, pow(max(dir.y, 0), HORIZON_POWER))`;
 * below 1 the horizon band gets wider, which is what makes a low-poly sky read as *sky* rather
 * than as a vertical wipe.
 */
const HORIZON_POWER = 0.42;

/** Starfield: frequency of the noise, and how hard it is crushed into isolated points. */
const STAR_FREQ = 190;
const STAR_SHARPNESS = 30;
const STAR_GAIN = 2.6;

/**
 * Environment intensity kept on the handle for compatibility. `envTexture` is always `null` now —
 * there is no PMREM and no IBL — so nothing reads this; see the header.
 */
const ENV_INTENSITY = 1;

// --- public surface ---------------------------------------------------------------------------

export interface SkyHandle {
  /** The dome mesh. Added to the scene by the caller. One draw, twelve triangles. */
  readonly mesh: Mesh;
  /**
   * Always `null`. There is no PMREM bake and no image-based lighting any more; the hemisphere
   * light is the whole ambient term. Kept so existing `sky.envTexture !== null` guards compile.
   */
  readonly envTexture: Texture | null;
  /** Inert — `envTexture` is null, so nothing multiplies this. Kept for compatibility. */
  readonly envIntensity: number;
  /** Unit vector pointing *toward the sun*, whatever the hour. The dome's disc uses this. */
  readonly sunDir: Vector3;
  /**
   * Unit vector pointing toward the **key light** — the sun while it is up, the moon while it is
   * not. This, not `sunDir`, is what the scene's `DirectionalLight` must follow, or the world is
   * lit from under the map for a third of every day.
   */
  readonly keyDir: Vector3;
  /** Key light colour: the ramp's `sun`, which is the bone-white moon at night. */
  readonly sunColor: Color;
  /**
   * The **key colour already multiplied by `keyIntensity`**, i.e. what the key actually
   * contributes rather than what colour it is.
   *
   * A `DirectionalLight` wants `sunColor` and `SUN_INTENSITY * keyIntensity` separately, and
   * scene.ts drives it that way. The *stylised* path cannot: `setStylisedSun` /`setCrowdSun` take
   * a colour and no intensity, so handing them `sunColor` lights every prop and every character
   * with a full-strength moon at midnight while the lit world around them correctly goes dark.
   * This field is that fix, and it is deliberately additive so the change on the scene side is one
   * identifier.
   */
  readonly keyColorScaled: Color;
  /** The `HemisphereLight`'s sky colour. (Not the dome's zenith — see `zenithColor`.) */
  readonly skyColor: Color;
  /** Ground-bounce colour. The `HemisphereLight`'s ground colour. */
  readonly hemiColor: Color;
  /** Top of the dome. Exposed for anything grading against the sky; the dome reads its uniform. */
  readonly zenithColor: Color;
  /** Bottom of the dome. Deliberately not `fogColor` — see the module header. */
  readonly horizonColor: Color;
  /**
   * Distance fog colour. `update` writes this, **and `near`/`far`**, into `scene.fog` every frame:
   * sight range is part of the colour story (60 m of night against 180 m of day), so the ramp owns
   * the range as well as the hue. All three are bound through `reference(...)` in `installFog`, so
   * they are uniforms and rewriting them costs nothing.
   */
  readonly fogColor: Color;
  /** Fog start distance in metres, from the ramp. Written into `scene.fog.near`. */
  readonly fogNear: number;
  /** Fog end distance in metres, from the ramp. Written into `scene.fog.far`. */
  readonly fogFar: number;
  /** Key intensity, 0..1, to be multiplied by the scene's design sun intensity. */
  readonly keyIntensity: number;
  /** Hemisphere fill, 0..1, to be multiplied by the scene's design ambient intensity. */
  readonly hemiIntensity: number;
  /** `HH:MM` in in-game hours, for anything that wants to print the world clock. */
  readonly clock: string;
  /** Advance to a wall-clock instant. Uniform writes only — never rebuilds a shader. */
  update(unixMs: number): void;
  /**
   * Installs plain range fog on the scene and takes ownership of its colour **and its range**:
   * every `update` writes `color`, `near` and `far` from the ramp.
   */
  installFog(scene: Scene): void;
  /** No-op. The Dimming is gone with the rest of the weather; kept so callers still compile. */
  setDimming(t: number): void;
  dispose(): void;
}

export interface SkyOptions {
  /**
   * Ignored. The world clock owns the sun now; this is kept so the existing `createSky(renderer,
   * { sunDir, tier })` call site in scene.ts compiles unchanged.
   */
  sunDir: Vector3;
  tier: QualityTier;
}

/** Mutable colour state, blended on the CPU once a frame. */
interface Ramp {
  sun: Color;
  zenith: Color;
  horizon: Color;
  sky: Color;
  hemi: Color;
  fog: Color;
  key: number;
  ambient: number;
  fogNear: number;
  fogFar: number;
  wash: number;
}

const _dirScratch = { x: 0, y: 1, z: 0 };

function hex(h: number): Color {
  return new Color().setHex(h, SRGBColorSpace);
}

function lerp(a: number, b: number, k: number): number {
  return a + (b - a) * k;
}

function lerpStop(out: Ramp, a: Ramp, b: Ramp, k: number): void {
  out.sun.copy(a.sun).lerp(b.sun, k);
  out.zenith.copy(a.zenith).lerp(b.zenith, k);
  out.horizon.copy(a.horizon).lerp(b.horizon, k);
  out.sky.copy(a.sky).lerp(b.sky, k);
  out.hemi.copy(a.hemi).lerp(b.hemi, k);
  out.fog.copy(a.fog).lerp(b.fog, k);
  out.key = lerp(a.key, b.key, k);
  out.ambient = lerp(a.ambient, b.ambient, k);
  out.fogNear = lerp(a.fogNear, b.fogNear, k);
  out.fogFar = lerp(a.fogFar, b.fogFar, k);
  out.wash = lerp(a.wash, b.wash, k);
}

function toRamp(s: SkyStop): Ramp {
  return {
    sun: hex(s.sun),
    zenith: hex(s.zenith),
    horizon: hex(s.horizon),
    sky: hex(s.sky),
    hemi: hex(s.hemi),
    fog: hex(s.fog),
    key: s.key,
    ambient: s.ambient,
    fogNear: s.fogNear,
    fogFar: s.fogFar,
    wash: s.wash,
  };
}

function emptyRamp(): Ramp {
  return {
    sun: new Color(),
    zenith: new Color(),
    horizon: new Color(),
    sky: new Color(),
    hemi: new Color(),
    fog: new Color(),
    key: 0,
    ambient: 0,
    fogNear: 0,
    fogFar: 1,
    wash: 0,
  };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function smootherstep01(v: number): number {
  const k = clamp01(v);
  return k * k * (3 - 2 * k);
}

/**
 * Builds the dome material. Everything the ramp touches is a uniform, so a whole day/night cycle
 * runs through this one compiled pipeline.
 */
function domeMaterial(u: {
  sunDir: AnyNode;
  zenith: AnyNode;
  horizon: AnyNode;
  fog: AnyNode;
  discTint: AnyNode;
  moonTint: AnyNode;
  sunGate: AnyNode;
  moonGate: AnyNode;
  wash: AnyNode;
}): NodeMaterial {
  const mat = new NodeMaterial();
  mat.side = BackSide;
  mat.depthWrite = false;
  mat.fog = false;

  // The same far-plane trick three's SkyMesh uses: the dome sits exactly on the far plane
  // whatever its scale, so it never fights the depth of anything real. Do not "tidy" this into a
  // `depthTest: false` — the dome would then paint over the world at every draw order.
  mat.vertexNode = Fn(() => {
    const position = modelViewProjection;
    position.z.assign(position.w);
    return position;
  })();

  mat.colorNode = Fn(() => {
    const dir = normalize(positionWorld.sub(cameraPosition));
    const s = normalize(u.sunDir);

    // Horizon -> zenith, with the horizon band widened so it reads as sky rather than a wipe.
    const up = max(dir.y, float(0));
    const grad = pow(up, HORIZON_POWER);
    const sky = mix(u.horizon, u.zenith, grad).toVar();

    // Stars: one noise, crushed to isolated points, gated on the same factor as the moon so they
    // arrive with it and are gone by mid-morning.
    const star = pow(mx_noise_float(dir.mul(STAR_FREQ)).mul(0.5).add(0.5), STAR_SHARPNESS);
    sky.addAssign(
      u.moonTint
        .mul(star)
        .mul(STAR_GAIN)
        .mul(u.moonGate)
        .mul(smoothstep(0, 0.12, dir.y)),
    );

    // Sun: disc, a tight halo, and a broad wash over that half of the sky.
    const cosS = dot(dir, s);
    const cosR = Math.cos(SUN_ANGULAR_RADIUS);
    const edge = 1 - cosR;
    const disc = smoothstep(float(cosR - edge * 0.35), float(cosR + edge * 0.1), cosS);
    const halo = pow(clamp(cosS, 0, 1), SUN_HALO_POWER).mul(SUN_HALO_GAIN);
    const wash = pow(clamp(cosS, 0, 1), SUN_WASH_POWER).mul(u.wash);
    sky.addAssign(u.discTint.mul(disc.mul(SUN_DISC_GAIN).add(halo).add(wash)).mul(u.sunGate));

    // Moon: the same shape, opposite the sun, larger, cool, and only present at night.
    const cosM = dot(dir, s.negate());
    const cosMR = Math.cos(MOON_ANGULAR_RADIUS);
    const mEdge = 1 - cosMR;
    const mDisc = smoothstep(float(cosMR - mEdge * 0.5), float(cosMR + mEdge * 0.15), cosM);
    // A touch of limb shading so the disc is a body rather than a hole punched in the sky.
    const mLimb = mix(float(0.72), float(1), smoothstep(float(cosMR), float(1), cosM));
    const mHalo = pow(clamp(cosM, 0, 1), MOON_HALO_POWER).mul(MOON_HALO_GAIN);
    sky.addAssign(u.moonTint.mul(mDisc.mul(mLimb).mul(MOON_DISC_GAIN).add(mHalo)).mul(u.moonGate));

    // Below the horizon line the terrain normally covers the dome; wherever it does not, fade to
    // the **fog** colour, not to a darkened horizon. That older form was only ever correct while
    // horizon and fog were the same colour: under Bonelight a `horizon * 0.22` band is a mid-grey
    // `#7E7B74` meeting sage-green fogged ground at the skyline, which reads as a seam. Fog is by
    // construction the colour everything at that distance already is.
    const below = smoothstep(float(-0.02), float(0.005), dir.y).oneMinus();
    return vec4(mix(sky, u.fog, below), 1);
  })();

  return mat;
}

/**
 * Builds the dome and the ramp. Nothing renders here — there is no bake any more, so this is a
 * few object allocations and one shader, and it is safe to call outside the loading screen.
 *
 * `renderer` and `opts.sunDir` are both unused; they are kept so scene.ts's existing call site
 * compiles unchanged.
 */
export function createSky(renderer: Renderer, opts: SkyOptions): SkyHandle {
  void renderer;
  void opts.sunDir;
  void opts.tier;

  const SUNRISE_R = toRamp(SUNRISE);
  const DAY_R = toRamp(DAY);
  const SUNSET_R = toRamp(SUNSET);
  const NIGHT_R = toRamp(NIGHT);
  const base = emptyRamp();
  const ramp = emptyRamp();

  const sunDir = new Vector3(0, 1, 0);
  const keyDir = new Vector3(0, 1, 0);
  const sunColor = new Color(1, 1, 1);
  const keyColorScaled = new Color(1, 1, 1);
  const skyColor = new Color(1, 1, 1);
  const hemiColor = new Color(1, 1, 1);
  const zenithColor = new Color(1, 1, 1);
  const horizonColor = new Color(1, 1, 1);
  const fogColor = new Color(1, 1, 1);
  /** The sun's *own* warm colour, independent of the night stop — the disc is never the moon's. */
  const discColor = new Color(1, 1, 1);
  const moonColor = hex(NIGHT.sun);

  const uSunDir = uniform(new Vector3(0, 1, 0));
  const uZenith = uniform(new Color());
  const uHorizon = uniform(new Color());
  const uFog = uniform(new Color());
  const uDiscTint = uniform(new Color());
  const uMoonTint = uniform(moonColor.clone());
  const uSunGate = uniform(0);
  const uMoonGate = uniform(0);
  const uWash = uniform(DAY.wash);

  const mesh = new Mesh(
    new BoxGeometry(1, 1, 1),
    domeMaterial({
      sunDir: uSunDir,
      zenith: uZenith,
      horizon: uHorizon,
      fog: uFog,
      discTint: uDiscTint,
      moonTint: uMoonTint,
      sunGate: uSunGate,
      moonGate: uMoonGate,
      wash: uWash,
    }),
  );
  mesh.name = 'sky';
  mesh.scale.setScalar(DOME_SCALE);
  mesh.frustumCulled = false;
  mesh.castShadow = false;
  mesh.receiveShadow = false;

  let fogScene: Scene | null = null;
  let clock = '00:00';
  let keyIntensity = 1;
  let hemiIntensity = 1;
  let fogNear = DAY.fogNear;
  let fogFar = DAY.fogFar;

  const handle: SkyHandle = {
    mesh,
    envTexture: null,
    envIntensity: ENV_INTENSITY,
    sunDir,
    keyDir,
    sunColor,
    keyColorScaled,
    skyColor,
    hemiColor,
    zenithColor,
    horizonColor,
    fogColor,
    get fogNear() {
      return fogNear;
    },
    get fogFar() {
      return fogFar;
    },
    get keyIntensity() {
      return keyIntensity;
    },
    get hemiIntensity() {
      return hemiIntensity;
    },
    get clock() {
      return clock;
    },

    update(unixMs: number): void {
      const t = timeOfDay(unixMs);
      sunDirection(t, _dirScratch);
      sunDir.set(_dirScratch.x, _dirScratch.y, _dirScratch.z).normalize();

      const nf = nightFactor(t);
      const tf = twilightFactor(t);
      // Day <-> night first, then the twilight stop on top. `t < 0.5` only ever flips at noon,
      // where `tf` is exactly 0, so the discriminator is never visible.
      lerpStop(base, DAY_R, NIGHT_R, nf);
      lerpStop(ramp, base, t < 0.5 ? SUNRISE_R : SUNSET_R, tf);

      sunColor.copy(ramp.sun);
      skyColor.copy(ramp.sky);
      hemiColor.copy(ramp.hemi);
      zenithColor.copy(ramp.zenith);
      horizonColor.copy(ramp.horizon);
      fogColor.copy(ramp.fog);
      fogNear = ramp.fogNear;
      fogFar = ramp.fogFar;
      // The disc keeps the sun's own warmth at every hour: the night stop's colour belongs to the
      // moon, and a bone-white sun at dawn would be the one thing the ramp cannot do.
      discColor.copy(DAY_R.sun).lerp(t < 0.5 ? SUNRISE_R.sun : SUNSET_R.sun, tf);

      // Whichever body is up leads the light. `lift` is 0 at the horizon for both of them, so the
      // 180-degree swing of `keyDir` happens while `keyIntensity` is 0.
      const el = sunElevation(t);
      const up = el >= 0;
      if (up) keyDir.copy(sunDir);
      else keyDir.set(-sunDir.x, -sunDir.y, -sunDir.z);
      const lift = Math.sin(Math.abs(el));
      const gate = smootherstep01(lift / HORIZON_GATE);
      keyIntensity = ramp.key * gate;
      hemiIntensity = ramp.ambient;
      keyColorScaled.copy(sunColor).multiplyScalar(keyIntensity);
      clock = clockText(t);

      uSunDir.value.copy(sunDir);
      uZenith.value.copy(zenithColor);
      uHorizon.value.copy(horizonColor);
      uFog.value.copy(fogColor);
      uWash.value = ramp.wash;
      uDiscTint.value.copy(discColor);
      // The disc fades out just under the horizon rather than at it, so the last sliver of sun
      // sets behind the dunes instead of blinking off.
      uSunGate.value = smootherstep01((Math.sin(el) + 0.05) / 0.09);
      // `nightFactor` is 0.5 at the crossing; a bare gate would hang a half-lit moon in the
      // middle of the sunset stop, competing with it. Hold the moon back until night has won.
      uMoonGate.value = smootherstep01((nf - 0.15) / 0.45);

      // Colour *and* range. `installFog` binds all three through `reference(...)`, so these are
      // uniform writes into the one compiled fog node — no material touched, nothing rebuilt.
      // Note this supersedes any per-map `near`/`far` a caller wrote at map-load time: sight
      // range is part of Bonelight's colour story (60 m at midnight against 180 m at noon) and
      // has to move with the ramp rather than with the map's bounding box.
      // `installFog` has already checked `isFog`, so the cast is the one it validated.
      const f = (fogScene?.fog ?? null) as Fog | null;
      if (f !== null) {
        f.color.copy(fogColor);
        f.near = fogNear;
        f.far = fogFar;
      }
    },

    installFog(scene: Scene): void {
      const f = scene.fog as Fog | null;
      if (f === null || (f as { isFog?: boolean }).isFog !== true) {
        console.warn(
          '[stillsun] sky.installFog: scene.fog is not a range Fog; keeping default fog',
        );
        return;
      }
      fogScene = scene;
      f.color.copy(fogColor);
      f.near = fogNear;
      f.far = fogFar;
      try {
        // Exactly the node three builds for an `isFog` scene by itself (see its
        // `Renderer`/`NodeLibrary` fog path). Written out here so the ramp's ownership of
        // `scene.fog.color` is explicit, and so a future height term has somewhere to go.
        const near = reference('near', 'float', f);
        const far = reference('far', 'float', f);
        const colour = reference('color', 'color', f);
        scene.fogNode = fog(colour, rangeFogFactor(near, far));
      } catch (err) {
        // Falling through leaves `scene.fogNode` unset, which makes three build the same node.
        console.warn('[stillsun] explicit fog node unavailable; using three’s default', err);
      }
    },

    setDimming(): void {
      /* The Dimming went with the weather. Kept so existing call sites compile. */
    },

    dispose(): void {
      mesh.geometry.dispose();
      (mesh.material as NodeMaterial).dispose();
    },
  };

  handle.update(Date.now());
  console.info(
    `[stillsun] sky ready · ${clock} in-game · key ${keyIntensity.toFixed(2)} · ` +
      `no IBL (envTexture null), 1 draw`,
  );
  return handle;
}
